import { useId, useState, type FormEvent } from 'react';
import type { PortalEditorView } from '../../../lib/api';
import { FIELD, LABEL, PRIMARY, Refusals, todayIso, type SaveChange } from './editorShared';

/**
 * VP9 - a deal's status and "where this stands". The state decides the section the card sits in (and its colour on the
 * map) - each option names that section - and "where this stands" is the line the client reads on the card. The
 * source is INTERNAL: it goes into the history, never onto the page. No figure is offered: the ask, the caps and the
 * rest are quoted from the documents.
 */
export default function DealStatusForm({ view, onSave }: { view: PortalEditorView; onSave: SaveChange }) {
  const id = useId();
  const [filter, setFilter] = useState('');
  const [dealId, setDealId] = useState('');
  const [state, setState] = useState('');
  const [label, setLabel] = useState('');
  const [asOf, setAsOf] = useState(todayIso());
  const [source, setSource] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [saved, setSaved] = useState('');
  const [saving, setSaving] = useState(false);

  const deal = view.deals.find((d) => d.id === dealId) ?? null;
  const sectionOf = (group: string | null) => view.states.find((s) => s.group === group)?.groupLabel ?? group ?? '';
  const needle = filter.trim().toLowerCase();
  const shown = view.deals.filter(
    (d) => d.id === dealId || !needle || `${d.name} ${d.city ?? ''}`.toLowerCase().includes(needle)
  );

  const pick = (next: string) => {
    const d = view.deals.find((x) => x.id === next);
    setDealId(next);
    setState(d?.state ?? '');
    setLabel(d?.label ?? '');
    setAsOf(todayIso());
    setSource('');
    setErrors([]);
    setSaved('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!deal) {
      setErrors(['Pick a deal first.']);
      return;
    }
    setSaving(true);
    const refused = await onSave({
      kind: 'status',
      target: deal.id,
      fields: { state, label, asOf },
      source: source.trim() ? source : null,
    });
    setSaving(false);
    if (refused) {
      setErrors(refused);
      setSaved('');
    } else {
      setErrors([]);
      setSaved(`Saved - the client will see Updated on ${deal.name}.`);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Deal status" className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Deal status</h3>
      <div className="grid gap-3 sm:grid-cols-[minmax(160px,1fr)_2fr]">
        <div>
          <label className={LABEL} htmlFor={`${id}-find`}>
            Find a deal
          </label>
          <input id={`${id}-find`} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Name or city" className={FIELD} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`${id}-deal`}>
            Deal
          </label>
          <select id={`${id}-deal`} value={dealId} onChange={(e) => pick(e.target.value)} className={FIELD}>
            <option value="">Choose a deal ({view.deals.length})</option>
            {shown.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {d.city ? ` - ${d.city}` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {deal && (
        <>
          <p className="text-xs text-gray-500">
            Now: {deal.label ?? '(no words)'}
            {deal.asOf ? ` (as of ${deal.asOf})` : ''} - {sectionOf(deal.group)}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL} htmlFor={`${id}-state`}>
                State
              </label>
              <select id={`${id}-state`} value={state} onChange={(e) => setState(e.target.value)} className={FIELD}>
                {view.states.map((s) => (
                  <option key={s.state} value={s.state}>
                    {s.state} - {s.groupLabel}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL} htmlFor={`${id}-asof`}>
                As of
              </label>
              <input id={`${id}-asof`} type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className={FIELD} />
            </div>
          </div>
          <div>
            <label className={LABEL} htmlFor={`${id}-label`}>
              Where this stands
            </label>
            <input id={`${id}-label`} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} className={FIELD} />
          </div>
          <div>
            <label className={LABEL} htmlFor={`${id}-source`}>
              Source (internal - never shown to the client)
            </label>
            <input
              id={`${id}-source`}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              maxLength={500}
              placeholder="e.g. the listing broker's email of Sep 12"
              className={FIELD}
            />
          </div>
          <button type="submit" disabled={saving} className={PRIMARY}>
            {saving ? 'Saving...' : 'Save status'}
          </button>
        </>
      )}
      <Refusals errors={errors} />
      {saved && (
        <p role="status" className="text-sm text-success">
          {saved}
        </p>
      )}
    </form>
  );
}
